import type { ApiClient } from '@/lib/api'
import type { Block, Tone } from '@/kova/spec/types'

/**
 * Where a screen's figures came from.
 *
 * # Fixture-ness is a property of a SCREEN, not of the console
 *
 * Today the shell shows one global "Fixtures" marker in the cell bar, which is
 * true and sufficient only while it is true of everything: nothing is wired to
 * `/v1/admin` yet, so no figure anywhere is real. The moment one screen reads
 * live data that marker becomes a lie in both directions — it either says
 * "Fixtures" over a real balance, or it disappears and implies the other 114
 * corpus screens are real. A single global badge cannot tell an operator which
 * of the numbers in front of them right now is a fact.
 *
 * So provenance travels with the data. Every screen answers, on its own,
 * whether what it drew came from the corpus or from the backend, and the badge
 * is rendered from that answer rather than from a build flag.
 *
 * # Freshness is structural, not optional
 *
 * `readAt` is required on both live variants, not just on analytics-sourced
 * ones. Law 12 only demands it for the analytics mirror, but an optional field
 * is a field that gets left out, and it gets left out exactly where a figure
 * looked current enough that nobody thought to stamp it. A ledger read has an
 * age too, and a three-minute-old provider snapshot presented without its age
 * is how a stale number gets acted on.
 *
 * # Absence is a state, never a substitution
 *
 * The `partial` variant exists so that a backend which could not answer for
 * some figures produces a screen that SAYS so. A missing figure is never
 * back-filled from the corpus and never rendered as zero — a zero is a claim,
 * and "€0.00 pending" and "we could not read pending" are different sentences
 * that lead to different operator actions. Same posture as `SessionState`'s
 * `unknown` in `@/lib/auth/session`: absence of an answer is not an answer.
 *
 * `unread` is the fourth variant and it was carved out of `partial`, which had
 * been doing both jobs. A screen whose endpoint answered 503 read NOTHING, and
 * describing that as "partial" claims some of it is real. The tooltip said
 * otherwise and the tooltip is not what gets read — the chip renders one word.
 * Degrees of not-knowing are worth separate variants for the same reason the
 * backend separates `not_built` from `unavailable`: an operator does something
 * different about each.
 */
export type DataSource =
  /** Design-corpus data. Describes no real customer, balance or queue. */
  | { readonly kind: 'corpus' }
  /**
   * The screen is designed and has no backend read behind it yet.
   *
   * The third state in the `available` / `not_built` / `unavailable` vocabulary
   * this console and the backend both use, and the last one to get a rendering.
   * It used to be dressed as `corpus` — a screen full of fixture rows carrying
   * a cautionary chip — which is how an invented table ends up in front of an
   * operator who did not re-read the header.
   *
   * DISTINCT FROM `unread`, and the distinction is law 20. `unread` is an
   * incident: something that should have answered did not, and the queues it
   * was meant to show are filling while nobody watches. This is a roadmap fact:
   * nothing is wrong, nothing is filling, and there is nothing for an operator
   * to escalate. Rendering the second as the first is how a real incident gets
   * ignored.
   */
  | { readonly kind: 'not_built'; readonly screen: string; readonly reason: string }
  /**
   * Everything on the screen was read from the backend.
   *
   * `source` names the store and relation an operator would have to query to
   * check us — `'postgres · ledger_postings'`, `'clickhouse · daily_flows'`.
   * `authority` names who the figures are answerable to, which is not the same
   * thing: our ledger is the operational system of record, and FinXP's records
   * still win every discrepancy (invariant 2).
   */
  | {
      readonly kind: 'live'
      readonly source: string
      readonly authority: string
      readonly readAt: string
    }
  /**
   * Read from the backend, but some figures were not answered for.
   *
   * `missing` names them in the operator's language, not the payload's —
   * `'provider available balance'`, not `'provider_available_minor'` — because
   * it is read on screen by someone deciding whether they can act.
   */
  | {
      readonly kind: 'partial'
      readonly source: string
      readonly authority: string
      readonly readAt: string
      readonly missing: readonly string[]
    }
  /**
   * The screen reads nothing until an operator runs it.
   *
   * # Why the other four all misstate this
   *
   * `rc-sim` is a probe rather than a report: `POST /recognition/simulate` is a
   * read that needs a body, so on arrival there is a control and no figures.
   * Every existing variant claims something false about that state — `live` and
   * `partial` both say *"read from X at T"* when nothing was read; `not_built`
   * says the screen has no backend behind it, and it has one it calls; and
   * `unread` is the worst of the four, because it is tone `r` and its own
   * comment calls it *"an incident… the operator is looking at a page that
   * knows nothing while the queues it was meant to show carry on filling"*. A
   * screen nobody has run yet is not an incident, and a red chip over one is
   * the habit law 20 exists to prevent.
   *
   * The precedent for adding a word rather than borrowing one is three
   * paragraphs down: `unread` was split out of `partial` because folding them
   * made the chip's single word claim *"some of this is real"* about a screen
   * where none of it was. This is that argument one step along — a screen where
   * none of it is real YET, and where that is the ordinary state rather than a
   * fault.
   *
   * `source` names what running it WILL call, because that is the question an
   * operator has about a screen with nothing on it.
   */
  | { readonly kind: 'probe'; readonly source: string; readonly reason: string }
  /**
   * The read did not happen. Nothing on the screen came from anywhere.
   *
   * Distinct from `partial`, which this used to be folded into, and the
   * distinction is the difference between two sentences an operator acts on
   * differently. `Partial` says *some of this is real* — and the chip's one
   * word is the part that gets read, ahead of the tooltip that qualified it.
   * A screen whose endpoint answered 503 had nothing real on it at all, and
   * calling that "partial" is the same overstatement in miniature that the
   * global Fixtures marker was in full.
   *
   * `source` is kept because it names what we TRIED — an operator diagnosing
   * this needs to know which endpoint failed — and `reason` says what came
   * back. There is deliberately no `readAt`: nothing was read, and stamping
   * the attempt would date figures that do not exist.
   */
  | {
      readonly kind: 'unread'
      readonly source: string
      readonly reason: string
    }

/**
 * A rendered screen and the account of where it came from.
 *
 * The two are one value on purpose. If provenance were passed alongside the
 * blocks, a screen could be rendered without it, and the failure mode of that
 * is silent: real-looking figures with no badge. Making them inseparable means
 * a loader cannot return data without saying what the data is.
 */
export interface ScreenData {
  readonly blocks: readonly Block[]
  readonly provenance: DataSource

  /**
   * What this screen is looking at, READ from the backend.
   *
   * # Why identity cannot come from the corpus
   *
   * The design corpus gives every screen a `title`, a `purpose` and a
   * `subject`, and the shell rendered them whether or not anything had been
   * read. On a list screen that is merely stale prose. On `cust-360` it was a
   * fabrication with a person's name on it: a customer id that resolves to
   * nothing still drew **"Lina Vogt · customers.id = 1042 · individual · DE ·
   * registered 2025-11-04"**, because those words are literals in
   * `screens/customers.ts` and no read had to succeed for them to appear.
   *
   * That is the worst failure this console has shipped. Every other corpus
   * artefact is a plausible row an operator might disbelieve; this one is an
   * identity, in the largest type on the page, over a record that does not
   * exist — and an operator who acts on it acts on the wrong person.
   *
   * So a live screen names itself from its own read or not at all. Absent here
   * means the shell falls back to the NAV LABEL — "Customer 360", a fact about
   * where you are standing — and never to the corpus, which is a claim about
   * what you are looking at.
   */
  readonly title?: string
  /** The sentence under the title. Same rule: read, or absent. */
  readonly purpose?: string
  /**
   * What the inspector is inspecting — `'customers · 1 row'`.
   *
   * The corpus set this to `'customers · 1,318 rows'` and the inspector drew it
   * beside a live table holding one row. A count is the easiest thing on a
   * screen to believe and the least likely to be re-checked.
   */
  readonly subject?: string

  /**
   * What each row of the screen's principal table IS, in row order.
   *
   * Optional, and absent is the default: most screens are a table an operator
   * reads and nothing more, and a screen that cannot be opened into should not
   * be asked to invent identities for its rows.
   *
   * It exists because a rendered cell is not an identity. The AML queue's first
   * column holds a `case_ref`, but it also holds `— no reference` for a case
   * whose ref did not arrive, and a caller that read the ref back out of the
   * cell would eventually request `/aml/cases/%E2%80%94%20no%20reference` and
   * report whatever answered as that operator's case. So the identity travels
   * beside the drawing rather than inside it, and `null` is the honest entry
   * for a row that has none.
   *
   * Positional, aligned with the rows of the FIRST table block — the same block
   * the cursor counts and the same one `rowCount` measures. A screen whose read
   * produced no table leaves this absent.
   */
  readonly rowKeys?: readonly (string | null)[]
}

/**
 * How a screen fetches itself.
 *
 * Takes the client rather than reaching for a module-level singleton so the
 * environment-bound config (Part I §4 — no default, no inference) stays the
 * caller's to supply, and so loaders are testable against a fake.
 *
 * A loader resolves; it does not throw for a backend that answered "I don't
 * know". An endpoint that is missing or degraded is a `partial`, or a refusal
 * the caller renders — not an exception that collapses the screen to nothing.
 */
export type ScreenLoader = (api: ApiClient) => Promise<ScreenData>

/**
 * Screen id → loader, for the screens that have a live endpoint.
 *
 * A screen absent from this map renders from the design corpus and badges
 * itself as corpus. That default is what makes the migration safe one screen at
 * a time (go-wallet#230 covers seven endpoints against 115 screens): forgetting
 * to register a loader leaves a screen honestly labelled fake, whereas a
 * default of "live" would leave it dishonestly labelled real.
 *
 * Registration happens here and nowhere else, so the set of screens claiming to
 * be live is one list a reviewer can read.
 */
export const LIVE_SCREENS: Readonly<Record<string, ScreenLoader>> = {}

/**
 * What the per-screen badge says.
 *
 * Tone choices are deliberate and each one is a claim:
 *
 * - Corpus is `a`, not `v`. The analytics tone means a real figure from the
 *   ClickHouse mirror carrying a freshness stamp (law 12) — a specific,
 *   truthful thing. Painting fixtures with it would make "this is invented"
 *   indistinguishable from "this is analytics", which is the one confusion this
 *   module exists to prevent.
 * - Corpus is not `r` either. It is currently the state of nearly every screen,
 *   and a console that is red everywhere has taught its operators that red
 *   means nothing.
 * - `live` is the only tone that reads as trustworthy, and only a screen that
 *   answered for all of its figures gets it.
 *
 * The label carries the meaning on its own. Tone is reinforcement, never the
 * only channel — an operator who cannot distinguish the colours must still be
 * able to read that the screen is not live.
 */
export function describeSource(d: DataSource): { label: string; tone: Tone; title: string } {
  switch (d.kind) {
    case 'corpus':
      return {
        label: 'Design corpus · not live',
        tone: 'a',
        title:
          'No figure on this screen came from the wallet API. Every count, amount and timestamp is design-corpus data from the KOVA prototype and describes no real customer, balance or queue.',
      }

    case 'not_built':
      // Tone `n`, not `a` and never `r`. The corpus chip was cautionary because
      // it sat over figures that could be misread; there are no figures here to
      // misread. A warning tone on a screen that is merely unfinished spends
      // an operator's attention on nothing and devalues the tones that matter.
      return {
        label: 'Not built · nothing to show',
        tone: 'n',
        title: `${d.reason} This screen is part of the designed console and has no backend read behind it, so it is deliberately blank rather than filled with example data. It is not an outage, not an empty queue and not a refusal — there is simply nothing here yet, and it will appear in the sidebar once it is connected.`,
      }

    case 'live':
      return {
        label: `Live · ${d.source}`,
        tone: 'g',
        title: `Read from ${d.source} at ${d.readAt}. Authority: ${d.authority}.`,
      }

    case 'probe':
      // Tone `n`, for the reason `not_built` is `n`: there are no figures here
      // to misread, so a cautionary tone would spend an operator's attention on
      // nothing and devalue the tones that matter. The label leads with what
      // the screen IS rather than with what it lacks — "nothing read" is the
      // true half that reads as a fault, and this screen has no fault.
      return {
        label: 'Probe · nothing read until you run it',
        tone: 'n',
        title: `${d.reason} This screen holds no figures until an operator runs it: it calls ${d.source}, which is a read that takes a body rather than a report that can be fetched. Nothing here is an outage, an empty result or a refusal — nothing has been asked yet.`,
      }

    case 'unread':
      // Critical, where `corpus` is only cautionary. A screen drawn from the
      // design corpus is a known, chosen state; a screen that could not read
      // its source is an incident, and the operator is looking at a page that
      // knows nothing while the queues it was meant to show carry on filling.
      return {
        label: 'Not read · nothing here is known',
        tone: 'r',
        title: `Nothing on this screen was read. ${d.reason} The read was against ${d.source}. This is not an empty queue, a clear day, or design-corpus data — it is the absence of an answer.`,
      }

    case 'partial': {
      // A `partial` that names nothing missing is a loader bug, and the honest
      // rendering of a loader bug is still "we do not know", never a clean
      // "Live" badge. Degrading to a vague sentence keeps the screen truthful
      // about being incomplete even when it failed to say how.
      const missing =
        d.missing.length === 0
          ? 'Some figures were not answered for.'
          : `Not answered: ${d.missing.join(', ')}.`

      return {
        label: `Partial · ${d.source}`,
        tone: 'a',
        title:
          `Read from ${d.source} at ${d.readAt}. Authority: ${d.authority}. ${missing} ` +
          'Those figures render as unavailable — never as zero, and never as a corpus value.',
      }
    }
  }
}

/**
 * The `readAt` stamp, in the format the design draws timestamps in.
 *
 * Takes `now` explicitly. Part III — pure helpers get their inputs handed to
 * them, no ambient clock, so they are table-testable; the corpus does the same
 * thing with `FIXTURE_CLOCK` rather than `new Date()`.
 *
 * UTC, always. Operators work across timezones against a provider that books on
 * its own value date, and a local-time stamp in a reconciliation argument is a
 * stamp two people read as two different instants.
 */
export function readAtStamp(now: Date): string {
  const iso = now.toISOString()
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`
}

/**
 * The read timestamp, when there genuinely is one.
 *
 * Exists so the two places that render a stamp — the header chip and the empty
 * state's "checked" line — cannot disagree about when there is nothing to
 * render. `unread` has no `readAt` field at all, and `corpus` is dated to the
 * design's fixed instant rather than to a read, so neither may produce one.
 */
export function readStampOf(source: DataSource): string | null {
  if (source.kind !== 'live' && source.kind !== 'partial') return null
  return source.readAt === '' ? null : source.readAt
}
